// @soft-depth
import React, { useState, useEffect, useMemo, useRef } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import {
  Activity,
  CircleHelp,
  Coins,
  FileText,
  Heart,
  Shield,
  Sprout,
  Stethoscope,
  TrendingUp,
  User
} from 'lucide-react';
import { decimalToString, formatNumber } from '../../../utils/formatNumber';
import ProRequiredBlur from '../../../components/SubscriptionComponents/ProRequiredDialog/ProRequiredBlur';
import ConstellationIcon from '../ConstellationIcon';
import PersonalityCard from './PersonalityCard';
import SetBirthdayPicker from './SetBirthdayPicker';
import IdeaTabs from './IdeaTabs';
import CoverQuizCard from '../../../views/CoverQuiz/CoverQuizCard';
import { useCoverQuiz } from '../../../views/CoverQuiz/coverQuizStorage';
import { COVER_ANIMALS, deriveCoverDisc } from '../../../views/CoverQuiz/coverQuizData';
import SmartIdeaCommunication from './SmartIdeaCommunication';
import { DiscPersonaCard, IdeaCard } from './IdeaKit';
import DobReadings from './DobReadings';
import DISCDialogComponent from '../DISCDialog/DISCDialogComponent/DISCDialogComponent';
import { DISC_EMOJI, discHint, discName, isDiscLetter } from '../../../constants/disc';
import { toggleDialog } from '../../../store/control/duck';
import {
  getConstellation,
  getCPIIntl,
  getLastMeetingMonthsDays,
  geToFactsheetDetails,
  isClientDisconnected,
  openChatGPTIceBreakingDrawer
} from '../utils';
import { getClientCoverage, getClientDetails, initSmartIdeaData, updateFactSheet } from '../../../store/client/duck';
import { getAgeFromDob } from '../../../utils/date';
import BottomSheetOrDialog from '../../BottomSheetOrDialog/BottomSheetOrDialog';
import { useHistoryChange, usePrevious, useWindowSize } from '../../../utils/hooks';
import { shouldEnableChatGPT } from '../../../utils/user';
import { ERROR, LOADING } from '../../../constants/phase';
import { useSeedClientNotes } from '../../../views/Advisor/ClientV2/clientNotesStore';
import { useNotesSaveFailedToast } from '../../../views/Advisor/ClientV2/useNotesSaveFailedToast';
import SoftDepthScope from '../../../design-system/SoftDepthScope';
import type { HueName } from '../../../design-system/tokens';

/**
 * 智能 IDEA — the client at a glance before a meeting: who they are, their
 * cover, their type and how to talk to them. Opened from the V1 client page,
 * the Business page and ClientV2 (which adds the 客戶概覽 / 溝通方式 tabs).
 *
 * Soft Depth, business mode, wherever it opens: it is an overlay, so it never
 * sits beside the legacy widgets of the page underneath. The legacy pieces it
 * still mounts — the DISC picker and the dialogs it opens — keep the legacy theme.
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale: string;
};

/** BottomSheetOrDialog is JavaScript behind injectIntl; named for the props passed here. */
type SurfaceProps = {
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  className?: string;
  header?: React.ReactNode;
  headerCTAs?: React.ReactNode[];
  content?: React.ReactNode;
  DialogProps?: Record<string, unknown>;
};
const Surface = BottomSheetOrDialog as unknown as React.ComponentType<SurfaceProps>;
const DiscPicker = DISCDialogComponent as unknown as React.ComponentType<Record<string, unknown>>;

// The Redux store and the JS utils are untyped.
export interface SmartIdeaDialogProps {
  intl: IntlLike;
  history: any;
  userDetails?: any;
  teamCampaignCodes?: any;
  coveragePhase?: unknown;
  noCoverageData?: any;
  smartIdeaDialog?: any;
  updateFactSheetPhase?: unknown;
  clientDetails?: any;
  toggleDialog: (name: string, value: any) => void;
  getClientCoverage: (args: { userId: string }) => void;
  initSmartIdeaData: () => void;
  getClientDetails: (...args: any[]) => void;
  updateFactSheet: (args: Record<string, unknown>) => void;
  userSubscription?: any;
}

// 潛力指數 → its signal colour, by the word getCPIIntl names it with.
const CPI_DOT: Record<string, string> = { High: 'cpi.high', Good: 'cpi.good', Fair: 'cpi.fair' };

// 婚姻 / 性別 / 子女 tags keep their own hues (blue / violet / green), as
// before the redesign, so each reads at a glance.
const hueTag = (hue: HueName) => ({
  fontWeight: 600,
  bgcolor: `hue.${hue}.soft`,
  borderColor: `hue.${hue}.border`,
  color: `hue.${hue}.dark`
});

// VIP / V.VIP: a gold tag where the old gold image sat, before the name, in
// the same shape as the other tags. V.VIP is the solid one.
const TIER_VVIP = {
  fontWeight: 700,
  bgcolor: 'hue.amber.dark',
  borderColor: 'hue.amber.dark',
  color: 'common.white'
};

/** One fact on the profile card: a muted label over its value. */
const Fact = (props: { label: React.ReactNode; children: React.ReactNode; wide?: boolean }) => (
  <Box sx={{ minWidth: 0, py: 0.75, ...(props.wide && { gridColumn: '1 / -1' }) }}>
    <Typography
      component="div"
      variant="subtitle2"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.secondary' }}
    >
      {props.label}
    </Typography>
    <Typography component="div" variant="body1" sx={{ fontWeight: 600, color: 'grey.800' }}>
      {props.children}
    </Typography>
  </Box>
);

/** A quiet grey well for "loading" / "disconnected". */
const Notice = (props: { children: React.ReactNode }) => (
  <Typography
    component="div"
    variant="body2"
    sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.100', textAlign: 'center', fontWeight: 600, color: 'text.secondary' }}
  >
    {props.children}
  </Typography>
);

/**
 * One cover type. Covered: its icon in the type's own hue (人壽 pink, 危疾
 * amber…, as before the redesign) and the amount in the accent. Not covered:
 * a 保障缺口, so the tile says it in the danger tint — with the words 無保障,
 * never by colour alone.
 */
interface CoverageTileProps {
  id: string;
  hue: HueName;
  icon: React.ReactNode;
  title: string;
  value?: React.ReactNode;
  empty: string;
}

const CoverageTile = (props: CoverageTileProps) => {
  const active = props.value !== undefined;
  return (
    <Box
      data-coverage={props.id}
      data-state={active ? 'active' : 'empty'}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: active ? 'divider' : 'error.border',
        bgcolor: active ? 'background.paper' : 'error.soft'
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 0.75,
          bgcolor: active ? `hue.${props.hue}.soft` : 'background.paper',
          color: active ? `hue.${props.hue}.main` : 'error.main'
        }}
      >
        {props.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'block', fontWeight: 700, color: 'text.secondary' }}
        >
          {props.title}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          noWrap
          sx={{ display: 'block', fontWeight: 700, color: active ? 'primary.dark' : 'error.dark' }}
        >
          {active ? props.value : props.empty}
        </Typography>
      </Box>
    </Box>
  );
};

const SmartIdeaDialog = (props: SmartIdeaDialogProps) => {
  const {
    intl,
    history,
    userDetails,
    teamCampaignCodes,
    coveragePhase,
    noCoverageData,
    smartIdeaDialog,
    updateFactSheetPhase,
    clientDetails,
    toggleDialog,
    getClientCoverage,
    initSmartIdeaData,
    getClientDetails,
    updateFactSheet,
    userSubscription
  } = props;

  const [hide, setHide] = useState(false);
  const prevSmartIdeaDialog = usePrevious(smartIdeaDialog);
  const prevUpdateFactSheetPhase = usePrevious(updateFactSheetPhase);
  const [innerWidth] = useWindowSize();
  const isWidescreen = innerWidth > 800;

  const initialPersonality = _.get(smartIdeaDialog?.client?.factsheetId, 'personality', undefined);
  const [localPersonality, setLocalPersonality] = useState(initialPersonality);
  // The persona card's 性格類型 picker. Declared up here, before the early return
  // below, so every render calls the same hooks in the same order.
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  useEffect(() => {
    setLocalPersonality(initialPersonality);
  }, [initialPersonality]);

  /**
   * Which card the caller opened this dialog *for*. ClientV2's DISC readout
   * sets it to 'personality', so the adviser lands looking at the card that
   * carries what they tapped instead of hunting for it in a dashboard of four.
   * Only that one card is marked and scrolled to; nothing else about the dialog
   * changes, and callers that omit it get exactly what they got before.
   */
  const focusSection = _.get(smartIdeaDialog, 'focusSection', undefined);
  const personalityCardRef = useRef<HTMLElement>(null);

  /**
   * ClientV2 opens this dialog with `communicationTab`, which splits it into
   * 客戶概覽 / 溝通方式 — the same two pages 招募 IDEA has, with the 性格類型
   * card moving to 溝通方式 beside 我的溝通風格. Other callers (V1) get the
   * single-page dashboard they always had.
   */
  const communicationTab = !!_.get(smartIdeaDialog, 'communicationTab');
  const dialogClientId = _.get(smartIdeaDialog, 'client._id');
  const [page, setPage] = useState<'overview' | 'communication'>('overview');

  // The adviser notes (異議處理's marks) arrive on the client's factsheet; this
  // dialog also runs on the V1 client page, where nothing else seeds the store.
  // Seed once per client opened: the snapshot is fresh from the Redux list at
  // open, and later edits inside the dialog replace the snapshot object with a
  // copy whose notes are older than the store's.
  const dialogClient = _.get(smartIdeaDialog, 'client');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seededClients = useMemo(() => (dialogClient ? [dialogClient] : null), [dialogClientId]);
  useSeedClientNotes(seededClients, _.get(props, 'userDetails.email'));

  // A failed adviser-notes write says so — shown once even with ClientV2's bridge listening too.
  useNotesSaveFailedToast(intl);

  // Without a letter on file, 溝通方式 reads the one the client's 保障小測驗
  // implies — 我的溝通風格 and the DISC 手冊 must agree on who they describe.
  const coverQuiz = useCoverQuiz(dialogClientId);
  const quizDisc = coverQuiz ? deriveCoverDisc(coverQuiz.answers) : null;

  // Each opening starts on 客戶概覽, unless it was opened for the DISC card.
  useEffect(() => {
    setPage(communicationTab && focusSection === 'personality' ? 'communication' : 'overview');
  }, [dialogClientId, communicationTab, focusSection]);

  useEffect(() => {
    if (focusSection !== 'personality' || !smartIdeaDialog || smartIdeaDialog.closed) return undefined;
    // After the dialog's own entrance, so the scroll lands where the card ends
    // up rather than where it started.
    const timer = setTimeout(() => {
      personalityCardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 260);
    return () => clearTimeout(timer);
  }, [focusSection, smartIdeaDialog]);

  const historyListener = useHistoryChange(() => {
    toggleDialog('smartIdea', false);
    initSmartIdeaData();
    historyListener?.();
  }, [toggleDialog, initSmartIdeaData]) as unknown as (() => void) | null; // the unlisten history.listen returned

  useEffect(() => {
    if (!prevSmartIdeaDialog && smartIdeaDialog) {
      const { client } = smartIdeaDialog;
      const isDisconnected = isClientDisconnected(client);
      if (!isDisconnected) {
        getClientCoverage({ userId: client._id });
      } else {
        initSmartIdeaData();
      }
    }
  }, [smartIdeaDialog, prevSmartIdeaDialog, getClientCoverage, initSmartIdeaData]);

  useEffect(() => {
    if (smartIdeaDialog && !smartIdeaDialog.closed) {
      if (prevUpdateFactSheetPhase !== updateFactSheetPhase) {
        if (updateFactSheetPhase === true && prevUpdateFactSheetPhase === LOADING) {
          toggleDialog('smartIdea', {
            ...smartIdeaDialog,
            client: {
              ...smartIdeaDialog?.client,
              factsheetId: {
                ...smartIdeaDialog?.client?.factsheetId,
                personality: _.get(clientDetails, 'personality', undefined),
                existingClient: _.get(clientDetails, 'existingClient', undefined)
              }
            }
          });
        }
      }
    }
  }, [updateFactSheetPhase, prevUpdateFactSheetPhase, smartIdeaDialog, toggleDialog, clientDetails]);

  // The dob set from this dialog is shown before the server answers; if that
  // write fails (transport error, or a 200 carrying `success: false`), put the
  // previous dob/age back so the dialog doesn't claim a birthday that isn't saved.
  const pendingDobRevertRef = useRef<{ clientId: string; dob: unknown; age: unknown } | null>(null);
  useEffect(() => {
    const revert = pendingDobRevertRef.current;
    if (!revert || prevUpdateFactSheetPhase !== LOADING || updateFactSheetPhase === LOADING) return;
    pendingDobRevertRef.current = null;
    const failed = updateFactSheetPhase === ERROR || updateFactSheetPhase === false;
    if (!failed || !smartIdeaDialog || smartIdeaDialog.closed) return;
    if (smartIdeaDialog.client?._id !== revert.clientId) return;
    toggleDialog('smartIdea', {
      ...smartIdeaDialog,
      client: {
        ...smartIdeaDialog.client,
        factsheetId: { ...smartIdeaDialog.client?.factsheetId, dob: revert.dob, age: revert.age }
      }
    });
  }, [updateFactSheetPhase, prevUpdateFactSheetPhase, smartIdeaDialog, toggleDialog]);

  const { client, closed, extraHeading } = smartIdeaDialog || {};

  if (!smartIdeaDialog) {
    return null;
  }

  const t = (id: string) => intl.formatMessage({ id });
  const factsheet = _.get(client, 'factsheetId', {});
  const isDisconnected = isClientDisconnected(client);
  const tier: string | undefined = ['VIP', 'V.VIP'].includes(factsheet.importance) ? factsheet.importance : undefined;
  const lastMeetingDate = factsheet.latestMeetingDate && !isDisconnected ? factsheet.latestMeetingDate : undefined;
  const { lastMeetingDurationMonths, lastMeetingDurationDays } = getLastMeetingMonthsDays(lastMeetingDate);
  const lastLoginDate = client?.lastLogin ? moment(client.lastLogin).format('DD-MMM-YYYY') : undefined;
  const showChatGPT = shouldEnableChatGPT({
    user: userDetails,
    teamCampaignCodes: teamCampaignCodes,
    userSubscription: userSubscription
  });

  const genderShort = () => {
    if (factsheet.gender === 'Male') {
      return t('M_word');
    }
    if (factsheet.gender === 'Female') {
      return t('F_word');
    }
    return '';
  };

  const CPIText = getCPIIntl(client);
  const dob = _.get(factsheet, 'dob');
  const dobMoment = dob ? moment(factsheet.dob) : undefined;
  const age = getAgeFromDob(dob, _.get(factsheet, 'age'));
  const constellation = getConstellation(dobMoment);
  const existingClient = _.get(factsheet, 'existingClient');
  const remark = _.get(factsheet, 'remark');

  const onClose = () => {
    toggleDialog('smartIdea', { ...smartIdeaDialog, closed: true });
    initSmartIdeaData();
  };

  const onExited = () => {
    if (!hide) {
      toggleDialog('smartIdea', false);
      setHide(false);
    }
  };

  const chatGPT = () => {
    openChatGPTIceBreakingDrawer(client);
  };

  const edit = () => {
    onClose();
    geToFactsheetDetails(client, history, getClientDetails);
  };

  // Sets the dob in place instead of leaving for the factsheet form. `age` rides
  // along because the server writes both from one request
  // (clientController.updateFactSheet) — see ClientV2Bridge.handleEditFact.
  const saveBirthday = (value: string) => {
    const factsheetId = _.get(smartIdeaDialog, 'client.factsheetId._id', undefined);
    if (!value || !factsheetId) return;
    const age = getAgeFromDob(value, undefined);
    pendingDobRevertRef.current = {
      clientId: client._id,
      dob: smartIdeaDialog.client?.factsheetId?.dob,
      age: smartIdeaDialog.client?.factsheetId?.age
    };
    toggleDialog('smartIdea', {
      ...smartIdeaDialog,
      client: {
        ...smartIdeaDialog.client,
        factsheetId: { ...smartIdeaDialog.client?.factsheetId, dob: value, age }
      }
    });
    updateFactSheet({
      id: factsheetId,
      key: 'dob',
      value,
      age: age ?? '',
      clientId: client._id
    });
  };

  // No factsheet to write to → nothing to set here; keep the old placeholder.
  const renderDobPicker = () =>
    !_.get(smartIdeaDialog, 'client.factsheetId._id') ? (
      '-'
    ) : (
      <SetBirthdayPicker hintText={t('set-birthday-first')} onPick={saveBirthday} />
    );

  const CPIHint = () => {
    setHide(true);
    toggleDialog('CPIHint', {
      onClose: () => setHide(false)
    });
  };

  const toggleExistingClient = (value: 'Yes' | 'No' | null) => {
    toggleDialog('smartIdea', {
      ...smartIdeaDialog,
      client: _.merge(smartIdeaDialog.client, {
        factsheetId: {
          existingClient: value
        }
      })
    });
    updateFactSheet({
      id: _.get(smartIdeaDialog, 'client.factsheetId._id', undefined),
      key: 'existingClient',
      value: value,
      clientId: client._id
    });
  };

  const changePersonality = (nextPersonality: string) => {
    setLocalPersonality(nextPersonality);

    updateFactSheet({
      id: _.get(smartIdeaDialog, 'client.factsheetId._id', undefined),
      key: 'personality',
      value: nextPersonality,
      clientId: client._id
    });
  };

  // 保障小測驗 → 匯入客戶資料: the answers the adviser ticked, merged into the
  // A.I. profile (factsheet.promptInput). Shown at once, like saveBirthday, so
  // the A.I. button in the header already sees it.
  const importCoverQuiz = (promptInput: unknown) => {
    const factsheetId = _.get(smartIdeaDialog, 'client.factsheetId._id', undefined);
    if (!factsheetId) return;
    toggleDialog('smartIdea', {
      ...smartIdeaDialog,
      client: {
        ...smartIdeaDialog.client,
        factsheetId: { ...smartIdeaDialog.client?.factsheetId, promptInput }
      }
    });
    updateFactSheet({ id: factsheetId, key: 'promptInput', value: promptInput, clientId: client._id });
  };

  // With tabs (ClientV2), the client's type is set and read on 客戶概覽's persona
  // card — the same card 招募 IDEA has — through the 性格類型 dialog.
  const canEditType = !!_.get(factsheet, '_id');
  const personaLetter = isDiscLetter(localPersonality) ? localPersonality : null;
  const clientPersona = (
    <DiscPersonaCard
      testId="client-persona"
      letter={personaLetter}
      emoji={personaLetter ? DISC_EMOJI[personaLetter] : undefined}
      name={personaLetter ? discName(personaLetter, intl.locale) : undefined}
      color={personaLetter ? COVER_ANIMALS[personaLetter].color : undefined}
      tagline={personaLetter ? discHint(personaLetter, intl.locale) : undefined}
      traits={personaLetter ? [t(`${personaLetter}_approach`)] : undefined}
      readings={<DobReadings intl={intl} dob={dob} renderBirthdayHint={renderDobPicker} />}
      readingsTitle={t('clientv2-recruit-persona-dob')}
      onSetType={canEditType ? () => setTypePickerOpen(true) : undefined}
      editLabel={t('clientv2-recruit-profile-edit')}
      setTypeLabel={t('choose-personality')}
      emptyText={t('smart-idea-persona-empty')}
    />
  );

  // V1's single page keeps its 性格類型 card (it has no persona card).
  const personalityCard = (
    <PersonalityCard
      intl={intl}
      title={t('personality-card-title-client')}
      cardRef={personalityCardRef}
      focused={focusSection === 'personality'}
      personality={localPersonality}
      onChangePersonality={changePersonality}
      dob={dob}
      renderBirthdayHint={renderDobPicker}
    />
  );

  const noCoverage = intl.formatMessage({ id: 'no-coverage' }) || '無保障';
  const perMonth = (amount: unknown) => `${formatNumber(amount, intl)} /${t('Month')}`;
  const has = (count: string) => !!noCoverageData && noCoverageData[count] > 0;
  const coverage: Omit<CoverageTileProps, 'empty'>[] = [
    {
      id: 'life',
      hue: 'pink',
      icon: <Heart size={16} />,
      title: t('Life'),
      value:
        has('lifeCount') && client && client.lifeCoverage >= 0 ? formatNumber(client.lifeCoverage, intl) : undefined
    },
    {
      id: 'ci',
      hue: 'amber',
      icon: <Activity size={16} />,
      title: t('C.I.'),
      value: has('illnessCount') && client && client.ciCoverage >= 0 ? formatNumber(client.ciCoverage, intl) : undefined
    },
    {
      id: 'medical',
      hue: 'emerald',
      icon: <Stethoscope size={16} />,
      title: t('Medical'),
      value: has('medicalCount') ? t(_.get(noCoverageData, 'medicalData')) : undefined
    },
    {
      id: 'saving',
      hue: 'violet',
      icon: <Coins size={16} />,
      title: t('Saving'),
      value: has('savingCount') ? perMonth(noCoverageData.savingData) : undefined
    },
    {
      id: 'ilas',
      hue: 'cyan',
      icon: <TrendingUp size={16} />,
      title: t('ILAS'),
      value: has('savingILASCount') ? perMonth(noCoverageData.ilasData) : undefined
    },
    {
      id: 'annuity',
      hue: 'indigo',
      icon: <Sprout size={16} />,
      title: t('Annuity'),
      value: has('annuityCount') ? perMonth(noCoverageData.annuityData) : undefined
    }
  ];

  // 現有客戶 Yes / No — a pair of option chips; tapping the one that is on clears it.
  const existingChip = (value: 'Yes' | 'No', icon: string, alt: string) => {
    const on = existingClient === value;
    return (
      <Chip
        component="button"
        type="button"
        clickable
        variant={on ? 'filled' : 'outlined'}
        color={on ? 'primary' : 'default'}
        aria-pressed={on}
        data-testid={`existing-client-${value.toLowerCase()}`}
        icon={<Box component="img" src={icon} alt={alt} sx={{ width: 18, height: 18 }} />}
        label={t(value)}
        onClick={() => toggleExistingClient(on ? null : value)}
      />
    );
  };

  const profileCard = (
    <IdeaCard
      id="profile"
      testId="smart-idea-card"
      icon={<User size={18} aria-hidden="true" />}
      title={t('client-overview-title') || '個人概覽'}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: 1.5,
          rowGap: 0.75,
          '@media (max-width: 600px)': { gridTemplateColumns: 'minmax(0, 1fr)' }
        }}
      >
        <Fact
          label={
            <>
              {t('C.P.I.')}
              <IconButton
                size="small"
                aria-label={t('C.P.I.')}
                onClick={CPIHint}
                sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
              >
                <CircleHelp size={16} aria-hidden="true" />
              </IconButton>
              :
            </>
          }
        >
          <Stack direction="row" component="span" sx={{ alignItems: 'center', gap: 0.75 }}>
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: CPI_DOT[CPIText] || 'grey.300',
                border: '1.5px solid',
                borderColor: 'background.paper',
                boxShadow: theme => `0 0 0 1px ${theme.palette.divider}`
              }}
            />
            <Typography component="span" variant="body2" sx={{ color: 'text.primary' }}>
              {t(CPIText)}
            </Typography>
          </Stack>
        </Fact>

        <Fact label={`${t('existing-client')}:`}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {existingChip('Yes', 'img/existingClient.svg', 'existing-client')}
            {existingChip('No', 'img/nonExistingClient.svg', 'non-existing-client')}
          </Stack>
        </Fact>

        <Fact label={`${t('DoB')}:`}>
          {dobMoment ? (
            <Stack direction="row" component="span" sx={{ alignItems: 'center', gap: 1 }}>
              <span>{dobMoment.format('DD-MMM-YYYY')}</span>
              {constellation && (
                <ConstellationIcon src={constellation.image} className={undefined} style={{ width: 16, height: 16 }} />
              )}
              <Typography component="span" variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                ({age})
              </Typography>
            </Stack>
          ) : (
            renderDobPicker()
          )}
        </Fact>

        <Fact label={`${t('Occupation')}:`}>{_.get(factsheet, 'occupation') || '-'}</Fact>

        <Fact label={`${t('Income')}:`}>
          {_.get(factsheet, 'monthlyIncome')
            ? `${decimalToString(_.get(factsheet, 'monthlyIncome'))} /${t('Month')}`
            : '-'}
        </Fact>

        <Fact label={`${t('Last Met')}:`}>
          {lastMeetingDate ? (
            <span>
              {lastMeetingDurationMonths} {t('Mth')} {lastMeetingDurationDays} {t('daycount')}
            </span>
          ) : (
            '-'
          )}
        </Fact>

        <Fact label={`${t('last-login')}:`}>{lastLoginDate || '-'}</Fact>

        {factsheet.tags && factsheet.tags.length > 0 && (
          <Fact wide label={`${t('Tag')}:`}>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {factsheet.tags.map((tag: { text: string; color?: string }) => (
                // The tag's own colour is data the adviser picked, so it stays.
                <Chip
                  key={tag.text}
                  size="small"
                  variant="outlined"
                  label={tag.text}
                  style={tag.color ? { color: tag.color, borderColor: tag.color } : undefined}
                />
              ))}
            </Stack>
          </Fact>
        )}
      </Box>
    </IdeaCard>
  );

  const coverageCard = (
    <IdeaCard
      id="coverage"
      testId="smart-idea-card"
      icon={<Shield size={18} aria-hidden="true" />}
      title={t('Coverage')}
    >
      {isDisconnected ? (
        <Notice>{t('Disconnected')}</Notice>
      ) : coveragePhase === true ? (
        <Box
          data-testid="smart-idea-coverage"
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1.5 }}
        >
          {coverage.map(item => (
            <CoverageTile key={item.id} {...item} empty={noCoverage} />
          ))}
        </Box>
      ) : (
        <Notice>Loading...</Notice>
      )}
    </IdeaCard>
  );

  const notesCard = (
    <IdeaCard
      id="notes"
      testId="smart-idea-card"
      icon={
        <Box component="span" sx={{ display: 'flex', color: 'note.main' }}>
          <FileText size={18} aria-hidden="true" />
        </Box>
      }
      title={t('Quick Notes')}
    >
      {/* Paper yellow, as before the redesign: advisers find their own notes by it. */}
      <Typography
        component="div"
        variant="body2"
        sx={{
          minHeight: 80,
          px: 1.75,
          py: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'note.border',
          bgcolor: 'note.soft',
          color: 'note.dark',
          fontWeight: 500,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}
      >
        {remark || '-'}
      </Typography>
    </IdeaCard>
  );

  const overview = (
    <Stack
      sx={{ gap: 2 }}
      {...(communicationTab && {
        id: 'smart-idea-panel-overview',
        role: 'tabpanel',
        'aria-labelledby': 'smart-idea-tab-overview'
      })}
    >
      {communicationTab && (
        // 「他是誰」 first, like 招募 IDEA: the 保障小測驗 (the freshest thing
        // known about them once played) and the persona card.
        <Stack sx={{ gap: 1.75 }}>
          <CoverQuizCard
            intl={intl}
            clientId={_.get(client, '_id')}
            clientName={_.get(factsheet, 'name', '')}
            currentDisc={localPersonality}
            onApplyDisc={_.get(factsheet, '_id') ? changePersonality : undefined}
            currentPromptInput={_.get(factsheet, 'promptInput')}
            onImport={_.get(factsheet, '_id') ? importCoverQuiz : undefined}
          />
          {clientPersona}
        </Stack>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '@media (min-width: 800px)': { display: 'grid', gridTemplateColumns: '0.9fr 1.1fr', alignItems: 'start' }
        }}
      >
        <Stack sx={{ gap: 2, minWidth: 0 }}>
          {profileCard}
          {coverageCard}
        </Stack>
        <Stack sx={{ gap: 2, minWidth: 0 }}>
          {/* The persona card on top replaces 性格類型 when the dialog has tabs. */}
          {!communicationTab && personalityCard}
          {notesCard}
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <div>
      <DiscPicker
        open={typePickerOpen}
        contentTextType="client"
        personality={personaLetter || undefined}
        onClose={() => setTypePickerOpen(false)}
        onSubmit={(letter: string) => {
          if (isDiscLetter(letter)) changePersonality(letter);
        }}
        // Above the Smart IDEA bottom sheet on mobile.
        style={{ zIndex: 2000 }}
      />
      <SoftDepthScope mode="business">
        <Surface
          className="smart-idea-modal"
          open={!!smartIdeaDialog && !hide && !closed}
          onClose={onClose}
          onExited={onExited}
          header={t('Smart IDEA')}
          DialogProps={{
            fullWidth: true,
            maxWidth: isWidescreen ? 'md' : 'sm'
          }}
          headerCTAs={[
            showChatGPT && (
              <IconButton key="ai" aria-label="A.I." onClick={chatGPT} sx={{ p: 0.25 }}>
                <img src="img/icons/ai.svg" alt="A.I. Pro" width={22} height={22} />
              </IconButton>
            ),
            // The filled pen, as on every other 編輯 in the app header.
            <IconButton key="edit" aria-label={t('Edit')} onClick={edit} sx={{ p: 0.5, color: 'primary.main' }}>
              <EditIcon sx={{ width: 18, height: 18 }} aria-hidden="true" />
            </IconButton>
          ]}
          content={
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'background.default' }}>
              <ProRequiredBlur>
                {extraHeading && (
                  <Typography color="error" align="center" sx={{ fontWeight: 500, mb: 2 }}>
                    {extraHeading}
                  </Typography>
                )}
                <Stack sx={{ gap: 2, mt: 1 }}>
                  {/* The client, named once at the top. */}
                  <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 2 }}>
                    {/* Where the VIP picture always sat: before the name. */}
                    {tier && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={tier}
                        data-testid="smart-idea-tier"
                        sx={tier === 'V.VIP' ? TIER_VVIP : { ...hueTag('amber'), fontWeight: 700 }}
                      />
                    )}
                    <Stack direction="row" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5, minWidth: 0 }}>
                      <Typography variant="h5" component="h3" sx={{ m: 0 }}>
                        {_.get(factsheet, 'name', '')}
                      </Typography>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {_.get(factsheet, 'marriageStatus') && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(_.get(factsheet, 'marriageStatus'))}
                            sx={hueTag('blue')}
                          />
                        )}
                        {genderShort() && (
                          <Chip size="small" variant="outlined" label={genderShort()} sx={hueTag('violet')} />
                        )}
                        {Number.parseInt(_.get(factsheet, 'child'), 10) >= 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${_.get(factsheet, 'child', '')} ${t('Child')}`}
                            sx={hueTag('emerald')}
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Paper>

                  {communicationTab && (
                    <IdeaTabs
                      tabs={[
                        { id: 'overview', label: t('smart-idea-overview-tab') },
                        { id: 'communication', label: t('smart-idea-communication-tab') }
                      ]}
                      value={page}
                      onChange={setPage}
                      idPrefix="smart-idea"
                      ariaLabel={t('Smart IDEA')}
                    />
                  )}

                  {communicationTab && page === 'communication' ? (
                    <Stack
                      id="smart-idea-panel-communication"
                      role="tabpanel"
                      aria-labelledby="smart-idea-tab-communication"
                      sx={{ gap: 1.75 }}
                    >
                      <SmartIdeaCommunication
                        intl={intl}
                        clientId={_.get(client, '_id')}
                        clientName={_.get(factsheet, 'name', '')}
                        disc={localPersonality || quizDisc}
                        personality={localPersonality}
                        facts={{
                          marriageStatus: _.get(factsheet, 'marriageStatus'),
                          monthlyIncome: _.get(factsheet, 'monthlyIncome'),
                          monthlyExpense: _.get(factsheet, 'monthlyExpense'),
                          existingClient,
                          age:
                            age !== undefined && age !== null && age !== '' && Number.isFinite(Number(age))
                              ? Number(age)
                              : undefined
                        }}
                        onGoToOverview={() => setPage('overview')}
                        playbookRef={personalityCardRef}
                      />
                    </Stack>
                  ) : (
                    overview
                  )}
                </Stack>
              </ProRequiredBlur>
            </Box>
          }
        />
      </SoftDepthScope>
    </div>
  );
};

const container = connect(
  // Map state to props
  (state: any) => ({
    noCoverageData: state.client.noCoverageData,
    coveragePhase: state.client.coveragePhase,
    smartIdeaDialog: state.control.smartIdeaDialog,
    updateFactSheetPhase: state.client.updateFactSheetPhase,
    clientDetails: state.client.clientDetails,
    userDetails: state.user.userDetails,
    teamCampaignCodes: state.functions.campaignCodes,
    userSubscription: state.userSubscription.userSubscription
  }),
  // Map actions to props
  {
    toggleDialog,
    getClientDetails,
    getClientCoverage,
    initSmartIdeaData,
    updateFactSheet
  }
)(withRouter(SmartIdeaDialog as any) as any);

export default injectIntl(container as any) as React.ComponentType<Record<string, unknown>>;
